import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2, CheckCircle2, HeartHandshake } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { v: "idea", t: "I have an idea" },
  { v: "just-started", t: "Just getting started" },
  { v: "established", t: "Already running my business" },
];
const GROUPS = [
  { v: "women", t: "Women in business" },
  { v: "men", t: "Men in business" },
  { v: "just-starting", t: "Just starting out" },
];

export default function CommunitySignupForm() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_name: "",
    business_stage: "just-started",
    group: "women",
    faith_background: "",
    hopes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || sent) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/community-signup`, form);
      setSent(true);
      toast({ title: "Welcome", description: "We'll reach out personally within one business day." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or email us directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-10 text-center" data-testid="cm-signup-success">
        <div className="w-14 h-14 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7 text-[color:var(--dms-navy)]" />
        </div>
        <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-5">Welcome, {form.name.split(" ")[0] || "friend"}.</h3>
        <p className="mt-3 text-[color:var(--dms-navy)]/70 max-w-md mx-auto">We'll be in touch personally within one business day to welcome you and share the next gathering details.</p>
        <p className="mt-6 font-serif italic text-[color:var(--dms-peach)]">Life is full of stories — Jesus is designing yours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 md:p-10" data-testid="cm-signup-form">
      <div className="flex items-center gap-3">
        <HeartHandshake className="w-5 h-5 text-[color:var(--dms-peach)]" />
        <p className="kicker">Community signup</p>
      </div>
      <h3 className="font-serif text-3xl text-[color:var(--dms-navy)] mt-3">Tell us a little about you</h3>
      <p className="mt-3 text-[color:var(--dms-navy)]/65 text-sm">So we can welcome you personally when you join.</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Your name *" testid="cm-field-name">
          <input required value={form.name} onChange={set("name")} className="field" placeholder="Jane Smith" />
        </Field>
        <Field label="Email *" testid="cm-field-email">
          <input required type="email" value={form.email} onChange={set("email")} className="field" placeholder="jane@example.com" />
        </Field>
        <Field label="Business name" testid="cm-field-business">
          <input value={form.business_name} onChange={set("business_name")} className="field" placeholder="Jane's Candle Co." />
        </Field>
        <Field label="Where are you in your business?" testid="cm-field-stage">
          <select value={form.business_stage} onChange={set("business_stage")} className="field">
            {STAGES.map((s) => <option key={s.v} value={s.v}>{s.t}</option>)}
          </select>
        </Field>
      </div>

      {/* Group */}
      <div className="mt-6">
        <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">I'd like to join as *</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {GROUPS.map((g) => (
            <button
              type="button"
              key={g.v}
              onClick={() => setForm({ ...form, group: g.v })}
              data-testid={`cm-group-${g.v}`}
              className={`text-left p-4 rounded-xl border-2 transition ${form.group === g.v ? "border-[color:var(--dms-peach)] bg-[color:var(--dms-peach-light)]/20" : "border-[color:var(--dms-navy)]/15 hover:border-[color:var(--dms-navy)]/35"}`}
            >
              <p className="font-serif text-base text-[color:var(--dms-navy)]">{g.t}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <Field label="Your faith background (so we can walk alongside you)" testid="cm-field-faith">
          <textarea rows={3} value={form.faith_background} onChange={set("faith_background")} className="field" placeholder="Where you are in your walk, your church, your season of life…" />
        </Field>
      </div>

      <div className="mt-6">
        <Field label="What do you hope to find here?" testid="cm-field-hopes">
          <textarea rows={3} value={form.hopes} onChange={set("hopes")} className="field" placeholder="Honest conversation, prayer partners, business advice, encouragement…" />
        </Field>
      </div>

      <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
        <p className="text-xs text-[color:var(--dms-navy)]/55">We read every reply ourselves — no automation.</p>
        <button type="submit" disabled={submitting} className="btn-peach" data-testid="cm-signup-submit">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request to join <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>

      <style>{`
        .field { width: 100%; border: 1px solid rgba(26,47,77,0.18); background: #fff; padding: 12px 14px; border-radius: 12px; font-size: 14px; color: #1a2f4d; outline: none; transition: border-color .2s; font-family: 'Inter', sans-serif; }
        .field:focus { border-color: var(--dms-peach); box-shadow: 0 0 0 3px rgba(212,165,116,0.15); }
      `}</style>
    </form>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
